import {
	isContractStatus,
	PAGES,
	PropositionStatus,
	PropositionStatusValues,
} from "@dune/types";
import { Button } from "@dune/ui";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@dune/ui";
import { RadioGroup, RadioGroupItem } from "@dune/ui";
import {
	getPropositionStatusBadgeClasses,
	getPropositionStatusLabel,
} from "@dune/utils";

import { ArrowLeft, XCircle } from "@untitled-ui/icons-react";
import { parseAsString, useQueryState } from "nuqs";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToggleSkipProposition } from "../../api/mutations/propositions.mutations";
import { useImportEmailAttachmentsProposition } from "../../api/mutations/mail.mutations";
import {
	useProposition,
	usePropositionCommunications,
	usePropositionDocuments,
	usePropositionTasks,
	usePropositionUnprocessedCommunicationsCount,
} from "../../api/queries/propositions.queries";
import { useMe } from "../../api/queries/users.queries";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutSkeleton,
	TabLayoutErrorSkeleton,
} from "../../component/layout/PageLayoutWithTabs";
import { useEntityPanelSync } from "../../hooks/useEntityPanelSync";
import { usePagination } from "../../hooks/usePagination";
import { useRequiredParams } from "../../hooks/useRequiredParams";
import { PropositionEmailComposerPanel } from "./components/panels/PropositionEmailComposerPanel";
import PropositionTaskDetailsPanel from "./components/panels/PropositionTaskDetailsPanel";
import { CommunicationsTab } from "./components/tabs/CommunicationsTab";
import { DocumentsTab } from "./components/tabs/DocumentsTab";
import { GeneralTab } from "./components/tabs/GeneralTab";
import { ReceiptsTab } from "./components/tabs/ReceiptsTab";
import { TasksTab } from "./components/tabs/TasksTab";
import { useQueryClient } from "@tanstack/react-query";
import { propositionsQueryKeys } from "../../api/query-keys/propositions.query-keys";
import SendDocumentModal from "./components/modals/SendDocumentModal";
import { RoleEnum } from "../../../../../packages/vars/src";
import { generatePropositionEmailWithFallback } from "../../utils/emails.utils";
import { useUpdatePropositionDuneComment } from "../../api/mutations/propositions.mutations";
import EntityDuneComment from "../../component/common/EntityDuneComment";

const PropositionPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useRequiredParams<{ id: string }>();
	const propositionId = Number.parseInt(id);
	const {
		data: proposition,
		isLoading,
		error,
		refetch,
	} = useProposition(propositionId);
	const [isSkipDialogOpen, setIsSkipDialogOpen] = useState(false);
	const [isUnskipDialogOpen, setIsUnskipDialogOpen] = useState(false);
	const [skipReason, setSkipReason] = useState("");
	const [isSendPropositionModalOpen, setIsSendPropositionModalOpen] =
		useState(false);
	const toggleSkipMutation = useToggleSkipProposition();
	const queryClient = useQueryClient();
	const { mutate: importEmailAttachments } =
		useImportEmailAttachmentsProposition(propositionId);

	const pagination = usePagination({
		initialLimit: 10,
		initialSortBy: "sentAt",
		initialSortOrder: "desc",
	});

	const { me } = useMe();
	const isSyndic = me?.role === RoleEnum.SYNDIC;
	const isInsurer = me?.role === RoleEnum.INSURER;
	const updateDuneCommentMutation = useUpdatePropositionDuneComment(propositionId);

	const {
		data: communicationsResponse,
		isLoading: commLoading,
		error: commError,
	} = usePropositionCommunications(propositionId, pagination.params);

	const handleLimitChange = (newLimit: number) => {
		pagination.setLimit(newLimit);
		pagination.setPage(1);
	};

	const { data: tasks } = usePropositionTasks(propositionId);

	const { data: propositionDocuments } = usePropositionDocuments(propositionId);

	const { data: unprocessedCommunicationsCount = 0 } =
		usePropositionUnprocessedCommunicationsCount(propositionId);

	const todoTasksCount = useMemo(() => {
		if (!tasks) return 0;
		return tasks.filter((task) => task.status === "TO_DO").length;
	}, [tasks]);

	const toProcessDocumentsCount = useMemo(() => {
		if (!propositionDocuments) return 0;
		return propositionDocuments.filter(
			(d) => d.file.humanProcessingStatus === "TO_BE_PROCESSED",
		).length;
	}, [propositionDocuments]);

	const tabs: Tab[] = useMemo(() => {
		if (isSyndic) {
			return [
				{ value: "general", label: "Général" },
				{
					value: "documents",
					label: "Documents",
					notificationCount: toProcessDocumentsCount,
				},
				{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
			];
		}
		if (isInsurer) {
			return [
				{ value: "general", label: "Général" },
				{ value: "receipts", label: "Quittances" },
				{
					value: "documents",
					label: "Documents",
					notificationCount: toProcessDocumentsCount,
				},
				{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
				{
					value: "communications",
					label: "Communications",
					notificationCount: unprocessedCommunicationsCount,
				},
				{ value: "activite", label: "Activité" },
			];
		}
		return [
			{ value: "general", label: "Général" },
			{ value: "receipts", label: "Quittances" },
			{
				value: "documents",
				label: "Documents",
				notificationCount: toProcessDocumentsCount,
			},
			{ value: "taches", label: "Tâches", notificationCount: todoTasksCount },
			{
				value: "communications",
				label: "Communications",
				notificationCount: unprocessedCommunicationsCount,
			},
			{ value: "activite", label: "Activité" },
		];
	}, [
		isSyndic,
		isInsurer,
		todoTasksCount,
		unprocessedCommunicationsCount,
		toProcessDocumentsCount,
	]);

	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault("general"),
	);

	const currentActiveTab = useMemo(() => {
		if (isSyndic && !["general", "documents", "taches"].includes(activeTab)) {
			return "general";
		}
		if (isInsurer && !["general", "receipts", "documents", "taches", "communications", "activite"].includes(activeTab)) {
			return "general";
		}
		return activeTab;
	}, [isSyndic, isInsurer, activeTab]);

	const panelComponents = useMemo(
		() => ({
			TaskDetails: PropositionTaskDetailsPanel,
			EmailComposer: PropositionEmailComposerPanel,
		}),
		[],
	);

	const panelEmailConfig = useMemo(
		() => ({
			getReplyToEmail: (entityId: number, emailRoutingUuid?: string | null) => {
				return generatePropositionEmailWithFallback(emailRoutingUuid, entityId);
			},
			importEmailAttachmentsHandler: (emailAttachmentId: number) => {
				importEmailAttachments({ emailAttachmentId });
			},
		}),
		[importEmailAttachments],
	);

	const panelTasks = useMemo(() => tasks ?? [], [tasks]);

	useEntityPanelSync({
		entityType: "PROPOSITION",
		entityId: propositionId,
		communications: communicationsResponse?.data ?? [],
		tasks: panelTasks,
		emailRoutingUuid: proposition?.emailRoutingUuid,
		components: panelComponents,
		emailConfig: panelEmailConfig,
	});

	useEffect(() => {
		if (!proposition) return;
		const currentStatus = proposition.status;
		if (isContractStatus(currentStatus)) {
			navigate(PAGES.CONTRACTS.show.withParams(id.toString()), {
				replace: true,
			});
		}
	}, [proposition, id, navigate]);

	const displaySignButton = useMemo(() => {
		if (!proposition || !tasks || isInsurer) return false;
		const sendProposition = tasks.find(
			(task) => task.templateKey === "SEND_PROPOSITION",
		);
		const generateQuittencement = tasks.find(
			(task) => task.templateKey === "GENERATE_QUITTANCEMENT",
		);
		return !!sendProposition && !generateQuittencement;
	}, [proposition, tasks, isInsurer]);

	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !proposition) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations de la proposition."
				tabCount={tabs.length}
			/>
		);
	}

	const handleSkipProposition = () => {
		toggleSkipMutation.mutate(
			{
				id: propositionId,
				skipReason,
			},
			{
				onSuccess: () => {
					refetch();
				},
			},
		);
		setIsSkipDialogOpen(false);
		setSkipReason("");
	};

	const handleUnskipProposition = () => {
		toggleSkipMutation.mutate(
			{
				id: propositionId,
			},
			{
				onSuccess: () => {
					refetch();
				},
			},
		);
		setIsUnskipDialogOpen(false);
	};

	const handleSkipDialogClose = () => {
		setIsSkipDialogOpen(false);
		setSkipReason("");
	};

	const handleUnskipDialogClose = () => {
		setIsUnskipDialogOpen(false);
	};

	const getSkipReasonLabel = (reason: string) => {
		switch (reason) {
			case "placed_elsewhere":
				return "J'ai placé l'affaire ailleurs";
			case "not_realized":
				return "Je n'ai pas réalisé l'affaire";
			default:
				return reason;
		}
	};

	const getStatusBadge = (status: PropositionStatusValues) => {
		const typed = status;
		const classes = getPropositionStatusBadgeClasses(typed);
		const label = getPropositionStatusLabel(typed);
		return (
			<div
				className={`inline-flex items-center px-2 py-1 rounded-full text-xs border whitespace-nowrap ${classes}`}
			>
				{label}
			</div>
		);
	};

	const tabContent: { [key: string]: React.ReactNode } = {
		general: <GeneralTab proposition={proposition} isSyndic={isSyndic} />,
		receipts: <ReceiptsTab proposition={proposition} />,
		documents: <DocumentsTab proposition={proposition} />,
		taches: <TasksTab proposition={proposition} />,
		communications: (
			<CommunicationsTab
				proposition={proposition}
				communicationsResponse={communicationsResponse}
				isLoading={commLoading}
				error={commError}
				onPageChange={pagination.setPage}
				onSortChange={pagination.handleSortChange}
				onLimitChange={handleLimitChange}
			/>
		),
		activite: <div className="p-6">Activité - À implémenter</div>,
	};

	const canBeSkipped =
		proposition.status === PropositionStatus.NOUVEAU ||
		proposition.status === PropositionStatus.A_SIGNER ||
		proposition.status === PropositionStatus.SIGNEE;

	return (
		<>
			{proposition.isSkipped && (
				<div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4">
					<div className="flex items-center">
						<div className="flex-shrink-0">
							<XCircle className="h-5 w-5 text-orange-400" />
						</div>
						<div className="ml-3">
							<p className="text-sm text-orange-700 font-medium">
								Classée sans suite
							</p>
							<p className="text-sm text-orange-600 mt-1">
								Raison : {getSkipReasonLabel(proposition.skipReason || "")}
							</p>
						</div>
					</div>
				</div>
			)}
			<PageLayoutWithTabs
				title={
					<div className="flex items-center gap-3">
						{proposition.reference}
						{getStatusBadge(proposition.status)}
					</div>
				}
			subtitle={
				me?.role === RoleEnum.DUNE && proposition.brokerSociety?.denomination
					? `Apporteur: ${proposition.brokerSociety.denomination}`
					: undefined
			}
			comment={
				me?.role === RoleEnum.DUNE ? (
					<EntityDuneComment
						initialComment={proposition.duneComment}
						onSave={(comment) =>
							updateDuneCommentMutation.mutate(comment)
						}
						isSaving={updateDuneCommentMutation.isPending}
					/>
				) : undefined
			}
			tabs={tabs}
				activeTab={currentActiveTab}
				onTabChange={setActiveTab}
				tabContent={tabContent}
				topActions={
					<div className="flex items-center gap-3 w-full justify-between">
						<Button
							variant="ghost"
							onClick={() => navigate(-1)}
							className="has-[>svg]:px-1"
						>
							<ArrowLeft />
							Retour
						</Button>
						<div className="flex items-center gap-3">
							{!isInsurer &&
								(proposition.isSkipped ? (
									<Dialog
										open={isUnskipDialogOpen}
										onOpenChange={setIsUnskipDialogOpen}
									>
										<DialogTrigger asChild>
											<Button variant="outline">
												Remettre la proposition en cours
											</Button>
										</DialogTrigger>
										<DialogContent className="sm:max-w-md">
											<DialogHeader>
												<DialogTitle>
													Remettre la proposition en cours
												</DialogTitle>
												<DialogDescription>
													Êtes-vous sûr de vouloir remettre cette proposition en
													cours ?
												</DialogDescription>
											</DialogHeader>
											<div className="grid grid-cols-2 gap-3">
												<Button
													variant="outline"
													className="w-full"
													onClick={handleUnskipDialogClose}
												>
													Annuler
												</Button>
												<Button
													variant="default"
													className="w-full"
													onClick={handleUnskipProposition}
													isLoading={toggleSkipMutation.isPending}
												>
													Remettre en cours
												</Button>
											</div>
										</DialogContent>
									</Dialog>
								) : (
									canBeSkipped && (
										<Dialog
											open={isSkipDialogOpen}
											onOpenChange={setIsSkipDialogOpen}
										>
											<DialogTrigger asChild>
												<Button variant="outline">
													Passer la proposition sans suite
												</Button>
											</DialogTrigger>
											<DialogContent className="sm:max-w-md">
												<DialogHeader>
													<DialogTitle>
														Passer la proposition sans suite
													</DialogTitle>
													<DialogDescription>
														Merci de sélectionner la raison :
													</DialogDescription>
												</DialogHeader>

												<RadioGroup
													value={skipReason}
													onValueChange={setSkipReason}
												>
													<div className="flex items-center space-x-2">
														<RadioGroupItem
															value="placed_elsewhere"
															id="placed_elsewhere"
															label="J'ai placé l'affaire ailleurs"
														/>
													</div>
													<div className="flex items-center space-x-2">
														<RadioGroupItem
															value="not_realized"
															id="not_realized"
															label="Je n'ai pas réalisé l'affaire"
														/>
													</div>
												</RadioGroup>

												<div className="grid grid-cols-2 gap-3">
													<Button
														variant="outline"
														className="w-full"
														onClick={handleSkipDialogClose}
													>
														Annuler
													</Button>
													<Button
														variant="default"
														className="w-full"
														onClick={handleSkipProposition}
														disabled={!skipReason}
														isLoading={toggleSkipMutation.isPending}
													>
														Passer sans suite
													</Button>
												</div>
											</DialogContent>
										</Dialog>
									)
								))}
							{displaySignButton && (
								<Button onClick={() => setIsSendPropositionModalOpen(true)}>
									Signature de la proposition
								</Button>
							)}
						</div>
					</div>
				}
			/>

			{/* Modal de signature de la proposition */}
			{isSendPropositionModalOpen && (
				<SendDocumentModal
					type={"PROPOSITION"}
					open={isSendPropositionModalOpen}
					onOpenChange={setIsSendPropositionModalOpen}
					propositionId={proposition.id}
					onSuccess={async () => {
						await queryClient.invalidateQueries({
							queryKey: propositionsQueryKeys.tasks(proposition.id),
							refetchType: "active",
						});
					}}
				/>
			)}
		</>
	);
};

export default PropositionPage;
